import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "wouter";
import { useTranslation } from "react-i18next";
import { Sakura } from "../components/shrine/Sakura";
import { OmikujiCard } from "../components/omikuji/OmikujiCard";
import { RARITY_DISPLAY, type Rarity } from "../lib/omikuji";
import {
  getDraws,
  getPairs,
  type StoredDraw,
  type StoredPair,
} from "../lib/storage";
import { useSound } from "../hooks/useSound";
import { useVibration, VibrationPatterns } from "../hooks/useVibration";

/**
 * §2.6 Collection
 *
 * - ペア毎 + ソロ の「アルバム」を表示
 * - 札のレアリティ別 / 引いた日順 に並び替え可能
 * - タップで札詳細モーダル (引いた日・相手・本文)
 * - ポケポケ参考: レア札は光って回転、通常札は静か
 */

type SortKey = "rarity" | "date";
type AlbumKey = string; // pairId or "__solo__"
const SOLO_ALBUM: AlbumKey = "__solo__";

export function Collection() {
  const { t, i18n } = useTranslation();
  const { unlock, play } = useSound();
  const { vibrate } = useVibration();

  const [draws, setDraws] = useState<StoredDraw[]>([]);
  const [pairs, setPairs] = useState<StoredPair[]>([]);
  const [activeAlbum, setActiveAlbum] = useState<AlbumKey | null>(null);
  const [sort, setSort] = useState<SortKey>("date");
  const [selected, setSelected] = useState<StoredDraw | null>(null);

  useEffect(() => {
    setDraws(getDraws());
    setPairs(getPairs());
  }, []);

  // アルバム一覧 (ペア + ソロ)
  const albums = useMemo(() => {
    const items: {
      key: AlbumKey;
      label: string;
      count: number;
      pair?: StoredPair;
    }[] = [];
    for (const p of pairs) {
      const count = draws.filter((d) => d.pairId === p.pairId).length;
      items.push({ key: p.pairId, label: p.partnerName, count, pair: p });
    }
    const soloCount = draws.filter((d) => d.mode === "solo").length;
    if (soloCount > 0 || items.length === 0) {
      items.push({
        key: SOLO_ALBUM,
        label: t("collection.soloAlbum"),
        count: soloCount,
      });
    }
    return items;
  }, [pairs, draws, t]);

  // 初回: 最初のアルバムを開く
  useEffect(() => {
    if (activeAlbum == null && albums.length > 0) setActiveAlbum(albums[0].key);
  }, [activeAlbum, albums]);

  const albumDraws = useMemo(() => {
    if (!activeAlbum) return [];
    const filtered =
      activeAlbum === SOLO_ALBUM
        ? draws.filter((d) => d.mode === "solo")
        : draws.filter((d) => d.pairId === activeAlbum);
    if (sort === "rarity") {
      return [...filtered].sort(
        (a, b) =>
          RARITY_DISPLAY[b.rarity].weight - RARITY_DISPLAY[a.rarity].weight ||
          b.drawnAt.localeCompare(a.drawnAt),
      );
    }
    return [...filtered].sort((a, b) => b.drawnAt.localeCompare(a.drawnAt));
  }, [activeAlbum, draws, sort]);

  function openCard(d: StoredDraw) {
    unlock();
    play("scrollOpen");
    vibrate(VibrationPatterns.cardOpen);
    setSelected(d);
  }

  function closeCard() {
    play("woodTap");
    setSelected(null);
  }

  return (
    <main className="relative min-h-dvh bg-[var(--color-night-deep)] text-[var(--color-washi)] overflow-hidden">
      {/* 背景 */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at center 90%, rgba(159,29,53,0.18), transparent 60%), linear-gradient(to bottom, #050203 0%, #0a0608 60%, #1a0c12 100%)",
        }}
      />
      <Sakura count={6} />

      <header className="relative z-10 px-6 pt-10 pb-4">
        <Link
          href="/home"
          className="text-[10px] tracking-[0.3em] text-[var(--color-gold-light)] opacity-70"
        >
          ← {t("common.back")}
        </Link>
        <h1
          className="mt-4 text-2xl tracking-[0.3em] text-[var(--color-gold-light)]"
          style={{
            fontFamily: "var(--font-fude)",
            textShadow: "0 0 18px rgba(201,169,97,0.4)",
          }}
        >
          {t("collection.title")}
        </h1>
      </header>

      {/* 何もない時 */}
      {draws.length === 0 && (
        <div className="relative z-10 px-6 pt-12 text-center">
          <p
            className="opacity-70 text-base leading-relaxed"
            style={{ fontFamily: "var(--font-fude)" }}
          >
            {t("collection.empty")}
          </p>
          <Link
            href="/draw"
            className="inline-block mt-8 px-8 py-3 rounded-full text-xs tracking-[0.3em] text-[var(--color-washi)]"
            style={{
              background:
                "linear-gradient(180deg, #c43a55 0%, #9f1d35 50%, #6b1024 100%)",
              border: "1px solid rgba(201,169,97,0.6)",
              boxShadow: "0 0 20px rgba(159,29,53,0.4)",
            }}
          >
            {t("solo.begin")}
          </Link>
        </div>
      )}

      {/* アルバムタブ */}
      {draws.length > 0 && (
        <>
          <div className="relative z-10 px-4 mt-2 overflow-x-auto no-scrollbar">
            <div className="flex gap-2 pb-2">
              {albums.map((a) => {
                const active = a.key === activeAlbum;
                return (
                  <button
                    key={a.key}
                    onClick={() => {
                      play("woodTap");
                      vibrate(8);
                      setActiveAlbum(a.key);
                    }}
                    className="px-4 py-2 rounded-full whitespace-nowrap text-xs tracking-widest transition active:scale-95"
                    style={{
                      fontFamily: "var(--font-fude)",
                      background: active
                        ? "linear-gradient(180deg, #c43a55, #9f1d35)"
                        : "rgba(10,6,8,0.7)",
                      border: active
                        ? "1px solid rgba(201,169,97,0.7)"
                        : "1px solid rgba(201,169,97,0.2)",
                      boxShadow: active
                        ? "0 0 18px rgba(159,29,53,0.45)"
                        : undefined,
                      color: active
                        ? "var(--color-washi)"
                        : "var(--color-gold-light)",
                    }}
                  >
                    {a.label}
                    <span className="opacity-60 ml-2">{a.count}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* 並び替え */}
          <div className="relative z-10 px-6 mt-2 flex items-center gap-3 text-[10px] tracking-widest text-[var(--color-gold-light)] opacity-80">
            <span>{i18n.language === "en" ? "Sort:" : "並び："}</span>
            <SortChip active={sort === "date"} onClick={() => setSort("date")}>
              {i18n.language === "en" ? "Date" : "日付"}
            </SortChip>
            <SortChip
              active={sort === "rarity"}
              onClick={() => setSort("rarity")}
            >
              {i18n.language === "en" ? "Rarity" : "レア度"}
            </SortChip>
          </div>

          {/* グリッド */}
          <ul className="relative z-10 mt-6 px-4 pb-32 grid grid-cols-2 gap-4">
            <AnimatePresence mode="popLayout">
              {albumDraws.length === 0 && (
                <motion.li
                  key="empty"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.6 }}
                  exit={{ opacity: 0 }}
                  className="col-span-2 text-center text-sm opacity-70 py-8"
                >
                  {activeAlbum === SOLO_ALBUM
                    ? t("collection.empty")
                    : t("collection.noPairs")}
                </motion.li>
              )}
              {albumDraws.map((d, i) => (
                <motion.li
                  key={d.drawId}
                  layout
                  initial={{ opacity: 0, y: 30, scale: 0.85 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.7 }}
                  transition={{
                    delay: Math.min(i * 0.04, 0.4),
                    duration: 0.5,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                >
                  <CardThumb draw={d} onTap={() => openCard(d)} />
                </motion.li>
              ))}
            </AnimatePresence>
          </ul>
        </>
      )}

      {/* 詳細モーダル */}
      <AnimatePresence>
        {selected && <DetailModal draw={selected} onClose={closeCard} />}
      </AnimatePresence>
    </main>
  );
}

function SortChip({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="px-2 py-1 rounded-full transition"
      style={{
        background: active ? "rgba(201,169,97,0.18)" : "transparent",
        border: active
          ? "1px solid rgba(201,169,97,0.6)"
          : "1px solid rgba(201,169,97,0.18)",
        color: active ? "var(--color-washi-warm)" : "var(--color-gold-light)",
      }}
    >
      {children}
    </button>
  );
}

function CardThumb({ draw, onTap }: { draw: StoredDraw; onTap: () => void }) {
  const rd = RARITY_DISPLAY[draw.rarity];
  const isRare =
    rd.weight >= 3 && draw.rarity !== "kyo" && draw.rarity !== "dai_kyo";

  return (
    <motion.button
      onClick={onTap}
      whileTap={{ scale: 0.95 }}
      whileHover={{ y: -4 }}
      className="w-full relative aspect-[3/4.5] rounded-lg overflow-hidden block text-left"
      style={{
        boxShadow: isRare
          ? `0 6px 18px rgba(0,0,0,0.6), 0 0 24px ${rd.glow}`
          : "0 6px 18px rgba(0,0,0,0.55)",
      }}
    >
      {/* 札本体 */}
      <div
        className="absolute inset-0 p-[2px] rounded-lg"
        style={{
          background:
            draw.rarity === "tokudai_kichi"
              ? "linear-gradient(135deg, #ff8a00, #c9a961, #ff8a00, #fffacd, #c9a961)"
              : draw.rarity === "dai_kichi"
                ? "linear-gradient(135deg, #c9a961, #fffacd, #c9a961)"
                : draw.rarity === "chu_kichi"
                  ? "linear-gradient(135deg, #c9a961, #8a6b2c)"
                  : draw.rarity === "kichi"
                    ? "linear-gradient(135deg, #c0c0c0, #8a8a8a)"
                    : draw.rarity === "kyo" || draw.rarity === "dai_kyo"
                      ? "linear-gradient(135deg, #1a1a1a, #4a4a4a)"
                      : "linear-gradient(135deg, #d4c8a8, #a89c7c)",
        }}
      >
        <div
          className="w-full h-full rounded-[6px] flex flex-col items-center justify-between py-3 px-2"
          style={{
            background:
              draw.rarity === "kyo" || draw.rarity === "dai_kyo"
                ? "linear-gradient(to bottom, #1a1a1a, #2a2a2a)"
                : "linear-gradient(to bottom, #faf6ee, #f0e8d4)",
            color:
              draw.rarity === "kyo" || draw.rarity === "dai_kyo"
                ? "#f5f1e8"
                : "#1a1a1a",
          }}
        >
          <span
            className="text-2xl"
            style={{
              fontFamily: "var(--font-fude)",
              color: rd.color,
              textShadow:
                draw.rarity === "tokudai_kichi"
                  ? "0 0 12px #ffd700"
                  : undefined,
            }}
          >
            {rd.ja}
          </span>
          <span className="text-[8px] opacity-60 tracking-widest">
            {formatShortDate(draw.date)}
          </span>
        </div>
      </div>

      {/* レア札はゆっくり光が回る */}
      {isRare && (
        <motion.div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          animate={{
            background: [
              `linear-gradient(45deg, transparent 30%, ${rd.glow} 50%, transparent 70%)`,
              `linear-gradient(225deg, transparent 30%, ${rd.glow} 50%, transparent 70%)`,
            ],
          }}
          transition={{ duration: 3.6, repeat: Infinity, ease: "easeInOut" }}
          style={{ mixBlendMode: "screen", opacity: 0.5 }}
        />
      )}

      {/* 特大吉は小さくふわふわ回転 */}
      {draw.rarity === "tokudai_kichi" && (
        <motion.div
          aria-hidden
          className="absolute inset-0 pointer-events-none rounded-lg"
          animate={{ rotate: [0, 1.5, -1.5, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        />
      )}
    </motion.button>
  );
}

function DetailModal({
  draw,
  onClose,
}: {
  draw: StoredDraw;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const body = i18n.language === "en" ? draw.bodyTextEn : draw.bodyText;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
      className="fixed inset-0 z-50 flex items-center justify-center px-6 backdrop-blur-md"
      style={{ background: "rgba(5,2,3,0.7)" }}
      onClick={onClose}
      role="dialog"
      aria-modal
    >
      <motion.div
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, scale: 0.85, y: 30 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-sm flex flex-col items-center gap-6"
      >
        <OmikujiCard
          rarity={draw.rarity}
          body={body}
          state="opened"
          size={200}
        />
        <div className="text-center px-2">
          <p className="text-[10px] tracking-[0.3em] text-[var(--color-gold-light)] opacity-80">
            {t("collection.drawn", {
              date: formatLongDate(draw.date, i18n.language),
            })}
          </p>
          {draw.partnerName && (
            <p
              className="mt-2 text-sm tracking-widest text-[var(--color-washi-warm)]"
              style={{ fontFamily: "var(--font-fude)" }}
            >
              ✦ {draw.partnerName} ✦
            </p>
          )}
          <p className="mt-2 text-[10px] tracking-widest opacity-60">
            {rarityLabel(draw.rarity, i18n.language)}
          </p>
        </div>
        <button
          onClick={onClose}
          className="px-8 py-2 rounded-full text-[10px] tracking-[0.3em]"
          style={{
            background: "rgba(10,6,8,0.7)",
            border: "1px solid rgba(201,169,97,0.4)",
            color: "var(--color-gold-light)",
          }}
        >
          {t("common.close")}
        </button>
      </motion.div>
    </motion.div>
  );
}

function formatShortDate(yyyyMmDd: string): string {
  const [, m, d] = yyyyMmDd.split("-");
  return `${m}/${d}`;
}

function formatLongDate(yyyyMmDd: string, locale: string): string {
  const [y, m, d] = yyyyMmDd.split("-");
  if (locale === "en") {
    const date = new Date(`${yyyyMmDd}T00:00:00`);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }
  return `${y}年${parseInt(m, 10)}月${parseInt(d, 10)}日`;
}

function rarityLabel(r: Rarity, locale: string): string {
  const rd = RARITY_DISPLAY[r];
  return locale === "en" ? rd.en : rd.ja;
}
